/**
 * Mascot — bottom-right SVG character reacting to game events via GSAP.
 */
import { useEffect, useLayoutEffect, useRef } from 'react';
import { gsap } from '@/lib/gsap';
import { useAppStore } from '@/lib/store';
import type { Grade } from '@/lib/score';

const MOUTH = {
  idle:  'M32 66 Q42 70 52 66',
  smile: 'M30 64 Q42 74 54 64',
  cheer: 'M28 62 Q42 80 56 62 Q42 70 28 62 Z',
  frown: 'M30 70 Q42 62 54 70',
} as const;

export function Mascot(): JSX.Element {
  const grade = useAppStore((s) => s.combo.lastGrade);
  const seq = useAppStore((s) => s.combo.burstSeq);
  const muted = useAppStore((s) => s.audioMuted);

  const bodyRef = useRef<SVGGElement>(null);
  const leftEyeRef = useRef<SVGGElement>(null);
  const rightEyeRef = useRef<SVGGElement>(null);
  const mouthRef = useRef<SVGPathElement>(null);
  const auraRef = useRef<SVGCircleElement>(null);
  const sparkleRef = useRef<SVGGElement>(null);

  // Idle breathing + blinking
  useLayoutEffect(() => {
    const body = bodyRef.current;
    const lEye = leftEyeRef.current;
    const rEye = rightEyeRef.current;
    if (!body || !lEye || !rEye) return;
    const tweens: gsap.core.Tween[] = [];

    tweens.push(gsap.to(body, {
      scaleY: 0.96, transformOrigin: '50% 100%',
      duration: 1.8, repeat: -1, yoyo: true, ease: 'sine.inOut',
    }));

    const blink = (): void => {
      gsap.to([lEye, rEye], {
        scaleY: 0.08, transformOrigin: 'center center',
        duration: 0.07, yoyo: true, repeat: 1, ease: 'power2.out',
      });
      gsap.delayedCall(3 + Math.random() * 3, blink);
    };
    const dc = gsap.delayedCall(2, blink);

    return () => {
      tweens.forEach((t) => t.kill());
      dc.kill();
    };
  }, []);

  // Reactive grade animation
  useEffect(() => {
    if (grade === null) return;
    playGradeAnim(grade);
  }, [seq, grade]);

  function playGradeAnim(g: Grade): void {
    const body = bodyRef.current;
    const mouth = mouthRef.current;
    const aura = auraRef.current;
    const sparkle = sparkleRef.current;
    const lEye = leftEyeRef.current;
    const rEye = rightEyeRef.current;
    if (!body || !mouth || !aura || !sparkle || !lEye || !rEye) return;

    const tl = gsap.timeline();

    if (g === 'perfect') {
      tl.to(body, { y: -22, scale: 1.15, duration: 0.32, ease: 'back.out(2.4)' }, 0);
      tl.to(body, { y: 0, scale: 1, duration: 0.45, ease: 'bounce.out' }, 0.32);
      tl.to(mouth, { attr: { d: MOUTH.cheer }, duration: 0.18, ease: 'expo.out' }, 0);
      tl.to([lEye, rEye], { scaleY: 1.2, scaleX: 1.2, duration: 0.18 }, 0);
      tl.fromTo(aura, { attr: { r: 12 }, opacity: 0.8 }, { attr: { r: 56 }, opacity: 0, duration: 0.7, ease: 'expo.out' }, 0);
      tl.fromTo(sparkle, { opacity: 1, scale: 0.4, transformOrigin: '50% 50%' }, { opacity: 0, scale: 1.6, rotate: 90, duration: 0.7, ease: 'expo.out' }, 0);
      tl.to(mouth, { attr: { d: MOUTH.idle }, duration: 0.3 }, 1.0);
      tl.to([lEye, rEye], { scaleY: 1, scaleX: 1, duration: 0.2 }, 1.0);
    } else if (g === 'great') {
      tl.to(body, { y: -10, duration: 0.22, ease: 'power3.out' }, 0);
      tl.to(body, { y: 0, duration: 0.32, ease: 'bounce.out' }, 0.22);
      tl.to(mouth, { attr: { d: MOUTH.smile }, duration: 0.18, ease: 'expo.out' }, 0);
      tl.to(mouth, { attr: { d: MOUTH.idle }, duration: 0.3 }, 0.9);
    } else if (g === 'good') {
      tl.to(body, { rotate: -4, duration: 0.18, transformOrigin: '50% 100%' }, 0);
      tl.to(body, { rotate: 0, duration: 0.32, ease: 'elastic.out(1, 0.4)' }, 0.18);
      tl.to(mouth, { attr: { d: MOUTH.smile }, duration: 0.18 }, 0);
      tl.to(mouth, { attr: { d: MOUTH.idle }, duration: 0.3 }, 0.7);
    } else {
      tl.to(body, {
        keyframes: [
          { x: -6, rotate: -3, duration: 0.06 },
          { x: 6, rotate: 3, duration: 0.06 },
          { x: -4, rotate: -2, duration: 0.06 },
          { x: 0, rotate: 0, duration: 0.08 },
        ],
        transformOrigin: '50% 100%',
      }, 0);
      tl.to(mouth, { attr: { d: MOUTH.frown }, duration: 0.18 }, 0);
      tl.to([lEye, rEye], { scaleY: 0.2, duration: 0.18 }, 0);
      tl.to([lEye, rEye], { scaleY: 1, duration: 0.3 }, 0.9);
      tl.to(mouth, { attr: { d: MOUTH.idle }, duration: 0.3 }, 0.9);
    }
  }

  return (
    <div
      // Mobile has a bottom nav strip — push Mascot off it and respect iOS
      // home-indicator safe-area. Use bottom-LEFT to avoid covering Combo HUD
      // (top-right) and primary CTAs (usually right-aligned).
      className="pointer-events-none fixed left-2 md:left-5 z-30 select-none opacity-50 md:opacity-70 hover:opacity-100 transition-opacity"
      style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 8px)' }}
      aria-hidden
    >
      <svg width="48" height="56" viewBox="0 0 84 96" className="overflow-visible md:w-[64px] md:h-[76px]">
        <defs>
          <linearGradient id="m-body" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#00f0ff" />
            <stop offset="100%" stopColor="#ff2e88" />
          </linearGradient>
          <filter id="m-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <circle ref={auraRef} cx="42" cy="52" r="20" fill="none" stroke="url(#m-body)" strokeWidth="2" opacity="0" />

        <g ref={sparkleRef} opacity="0">
          {[0, 60, 120, 180, 240, 300].map((deg) => {
            const r = 36;
            const x = 42 + Math.cos((deg * Math.PI) / 180) * r;
            const y = 52 + Math.sin((deg * Math.PI) / 180) * r;
            return (
              <g key={deg} transform={`translate(${x}, ${y}) rotate(${deg})`}>
                <path d="M0 -6 L1.5 -1.5 L6 0 L1.5 1.5 L0 6 L-1.5 1.5 L-6 0 L-1.5 -1.5 Z" fill="#aaff00" />
              </g>
            );
          })}
        </g>

        <g ref={bodyRef} filter="url(#m-glow)">
          <rect x="18" y="32" width="48" height="48" rx="18" fill="#070710" stroke="url(#m-body)" strokeWidth="2.5" />
          <g ref={leftEyeRef} transform="translate(32, 52)">
            <circle r="3.5" fill="#00f0ff" />
          </g>
          <g ref={rightEyeRef} transform="translate(52, 52)">
            <circle r="3.5" fill="#00f0ff" />
          </g>
          <path ref={mouthRef} d={MOUTH.idle} fill="none" stroke="#ff2e88" strokeWidth="2.2" strokeLinecap="round" />
          <line x1="42" y1="32" x2="42" y2="22" stroke="url(#m-body)" strokeWidth="2" />
          <circle cx="42" cy="20" r="3" fill="#aaff00" />
        </g>

        {muted && (
          <g transform="translate(70, 16)">
            <circle r="8" fill="#161624" stroke="#ff2e88" strokeWidth="1" />
            {/* Inline SVG mute icon (matches Lucide VolumeX) */}
            <g transform="translate(-5,-5) scale(0.42)" stroke="#ff2e88" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="#ff2e88" />
              <line x1="22" y1="9" x2="16" y2="15" />
              <line x1="16" y1="9" x2="22" y2="15" />
            </g>
          </g>
        )}
      </svg>
    </div>
  );
}
